import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import cookie from 'js-cookie';

/**
 * PDFViewer Component
 * Fetches PDF as blob and displays it using an iframe with object URL.
 * This prevents auto-download by avoiding Content-Disposition: attachment.
 *
 * @param {Object} props
 * @param {string} props.documentUrl - Authenticated URL to the PDF document
 * @param {string} props.filename - Name of the PDF file
 * @returns {React.ReactElement} PDF viewer
 */
const PDFViewer = ({ documentUrl, filename }) => {
    const theme = useTheme();
    const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let objectUrl = null;
        let isMounted = true;

        const fetchPdf = async () => {
            try {
                // Fetch PDF as blob with authentication
                const response = await axios.get(documentUrl, {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                    responseType: 'blob',
                });

                if (!isMounted) return;

                // Create object URL from blob
                const blob = new Blob([response.data], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(blob);
                setPdfObjectUrl(objectUrl);
            } catch (err) {
                if (isMounted) {
                    console.error('Error loading PDF:', err);
                    setError('Failed to load PDF. Please try downloading it instead.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (documentUrl) {
            fetchPdf();
        }

        // Cleanup: revoke object URL when component unmounts
        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [documentUrl]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: theme.palette.grey[50],
                    gap: 2,
                }}
            >
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    Loading PDF document...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: { xs: 2, sm: 3 },
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Alert
                    severity="error"
                    sx={{
                        maxWidth: 600,
                        width: '100%',
                        boxShadow: theme.shadows[2],
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Unable to Load PDF
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: theme.palette.grey[200],
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: { xs: '100%', md: '98%' },
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: { xs: 'none', md: theme.shadows[4] },
                    display: 'flex',
                    my: { xs: 0, md: 2 },
                    borderRadius: { xs: 0, md: 1 },
                    overflow: 'hidden',
                }}
            >
                <iframe
                    src={pdfObjectUrl}
                    title={filename}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    allow="fullscreen"
                />
            </Box>
        </Box>
    );
};

PDFViewer.propTypes = {
    documentUrl: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
};

export default PDFViewer;
